/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, zip } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { catchError, distinctUntilChanged, map, mergeMap, skip, take } from 'rxjs/operators';
import { HelperService } from '../../../services/helper/helper.service';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { PatientSharedService } from '../../../services/shared/patient-shared/patient-shared.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { StudyActionTypes } from '../../../store/study/study.action';
import { UserStore } from '../../../store/user/component-store/user.store';
import { OrganisationInterface } from '../../../models/interface/organisation/organisation.interface';
import { OrganisationActionTypes } from '../../../store/organisation/organisation.action';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

/**
 * Component:
 * Patient study page displaying all common studies with the ecoach;
 * Can be found: {uri}/patients/{{user_id}}/studies
 */
@Component({
  selector: 'app-patient-study',
  templateUrl: './patient-study.component.html',
  styleUrls: ['./patient-study.component.scss'],
  providers: [UserStore],
})
export class PatientStudyComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Filter
  public filter = { searchFilter: '' };

  public isLoading$: Observable<boolean>;

  public searchText$ = new BehaviorSubject<string>('');

  public studies$: Observable<StudyInterface[]>;

  public pagedStudies$: Observable<StudyInterface[]>;

  public isManager$: Observable<boolean>;

  public patient: UserInterface;

  public organisations$: Observable<OrganisationInterface[]>;
  public organisations: OrganisationInterface[] = [];

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  // Data provided by UserService
  private initialStudies: StudyInterface[];

  private collaboratingStudies: StudyInterface[] = [];

  private studiesSubject = new BehaviorSubject<StudyInterface[]>([]);
  private pagedStudiesSubject = new BehaviorSubject<StudyInterface[]>([]);
  private studiesDataSubject = new BehaviorSubject<PaginationData<StudyInterface>>({ items: [] });

  private isManagerSubject = new BehaviorSubject<boolean>(false);

  private memberStudies$: Observable<StudyInterface[]>;
  studiesData$: Observable<PaginationData<StudyInterface>>;

  // Subscription Handler
  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private sharedService: PatientSharedService,
    private actions$: Actions,
    private userStore: UserStore,
    private store: Store<{ organisations: OrganisationInterface[] }>,
    private translateService: TranslateService,
  ) {
    this.organisations$ = store.select('organisations');
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.studies$ = this.studiesSubject.asObservable();
    this.pagedStudies$ = this.pagedStudiesSubject.asObservable();
    this.studiesData$ = this.studiesDataSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.memberStudies$ = this.userStore.memberStudies$;
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.isLoadingSubject.next(true);
    this.isManagerSubject.next(false);

    this.subscriptions.push(
      this.sharedService.patient$.subscribe((value: UserInterface) => {
        // Due to async service check whether value is null or valid id
        if (value === null || value === undefined) {
          this.isLoadingSubject.next(true);
        } else {
          this.patient = value;
          this.applyStudyFilter();
        }
      })
    );

    // Subscription to language change
    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        this.applyStudyFilter();
      })
    );

    // Active nav is collaborators
    this.sharedService.pushActiveChild('studies');
  }

  public applyStudyFilter(setFirst?: boolean): void {
    this.isLoadingSubject.next(true);
    const organisations = this.reloadOrganisationList();
    const studies = this.reloadStudyList(setFirst);
    this.subscriptions.push(
      // Using zip, because forkJoin can be canceled by duplicate call of either request from other components
      zip(organisations, studies).subscribe(() => this.isLoadingSubject.next(false)),
    );
  }

  public reloadStudyList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratingStudiesType,
      payload: { type: 'study,organisation', include: 'owners,roles' },
    });
    const studies = this.actions$.pipe(
      ofType(
        StudyActionTypes.getCollaboratingStudiesSuccessType,
        StudyActionTypes.getCollaboratingStudiesErrorType,
      ),
      take(1),
      map(
        (result: any): StudyInterface[] =>
          result.type === StudyActionTypes.getCollaboratingStudiesSuccessType
            ? result.response.body.data
            : [],
      ),
    );
    this.userStore.getMemberStudies({ userId: this.patient.id });
    const memberStudies = this.memberStudies$.pipe(skip(1), take(1));
    return forkJoin({ studies, memberStudies }).pipe(
      mergeMap((results) => {
        this.collaboratingStudies = results.studies;
        this.initialStudies = results.memberStudies.filter((memberStudy: StudyInterface) =>
          this.collaboratingStudies.some((study) => study.id.toString() === memberStudy.id.toString()),
        );
        return this.search(this.filter.searchFilter).pipe(
          mergeMap(
            () => this.helper.setPagedContent(
              this.studiesSubject,
              this.pagedStudiesSubject,
              setFirst,
              pageIndex,
              pageSize,
              this.studiesDataSubject,
            ),
          ),
        );
      }),
    );
  }

  public reloadOrganisationList(): Observable<boolean> {
    this.store.dispatch({
      type: OrganisationActionTypes.getOrganisationsType,
      payload: { include: 'owners,collaborators,roles' },
    });
    return this.organisations$.pipe(
      skip(1),
      take(1),
      mergeMap((result) => {
        this.organisations = result;
        return of(true);
      }),
      catchError(() => {
        this.organisations = [];
        return of(false);
      }),
    );
  }

  public updatePagedStudies(event: StudyInterface[]) {
    if (event) {
      this.pagedStudiesSubject.next(event);
    }
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchText$.next(name);
    return this.searchText$.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        const searchTerm = this.filter.searchFilter.trim().toLowerCase();
        this.studiesSubject.next(
          this.helper.filterStudiesBy(this.initialStudies, searchTerm),
        );
        return of(true);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
